import { useState } from "react";
import { useInitialEffect } from "core/react-utils";
import { ActionType, Authorization, AuthorizationProvider, AuthReason, EventType } from "models/settings";
import { observer } from "mobx-react-lite";
import { Button, Form, Icon, Input, Modal } from "semantic-ui-react";
import { doRepositoryAuthorize, repositoryAuthorize } from "core/auth0";
import * as Storage from "core/storage";
import { AppStore, ModalStore, SettingsStore } from "stores";
import BitBucketServerForm from "models/forms/bit-bucket-server-form";
import AzureDevOpsForm from "models/forms/azure-dev-ops-form";
import { getSlackAuthUrl } from "core/slack";
import MicrosoftTeamsForm from "models/forms/microsoft-teams-form";
import AlertForm from "models/forms/alert-form";
import { runInAction } from "mobx";
import { getJiraAuthUrl } from "core/jira";

const bitBucketServerForm = new BitBucketServerForm();
const azureDevOpsForm = new AzureDevOpsForm();
const microsoftTeamsForm = new MicrosoftTeamsForm();

interface IntegrationConfig {
  key: string;
  name: string;
  provider: AuthorizationProvider;
  description: string;
  logo: string;
}

const oauthProviders = new Set([
  AuthorizationProvider.GitHub,
  AuthorizationProvider.Gitlab,
  AuthorizationProvider.BitBucket,
  AuthorizationProvider.Slack,
  AuthorizationProvider.Jira,
]);

const integrations: IntegrationConfig[] = [
  {
    key: "github",
    name: "GitHub",
    provider: AuthorizationProvider.GitHub,
    description: "Connect your GitHub repositories to automatically trigger test runs on code changes.",
    logo: "/img/integrations/github.svg",
  },
  {
    key: "gitlab",
    name: "GitLab",
    provider: AuthorizationProvider.Gitlab,
    description: "Connect your GitLab repositories to automatically trigger test runs on code changes.",
    logo: "/img/integrations/gitlab.svg",
  },
  {
    key: "bitbucket",
    name: "BitBucket",
    provider: AuthorizationProvider.BitBucket,
    description: "Connect your BitBucket repositories to automatically trigger test runs on code changes.",
    logo: "/img/integrations/bitbucket.svg",
  },
  {
    key: "bitbucket-server",
    name: "BitBucket Server",
    provider: AuthorizationProvider.BitBucketServer,
    description: "Connect a self-hosted BitBucket Server instance using a personal access token.",
    logo: "/img/integrations/bitbucket-server.svg",
  },
  {
    key: "azure-devops",
    name: "Azure DevOps",
    provider: AuthorizationProvider.AzureDevOps,
    description: "Connect Azure Repos with a personal access token to run tests from your repositories and post status checks to pull requests.",
    logo: "/img/integrations/azure-devops.svg",
  },
  {
    key: "microsoft-teams",
    name: "Microsoft Teams",
    provider: AuthorizationProvider.MicrosoftTeams,
    description: "Send test run notifications to a Microsoft Teams channel via webhook.",
    logo: "/img/integrations/microsoft-teams.svg",
  },
  {
    key: "slack",
    name: "Slack",
    provider: AuthorizationProvider.Slack,
    description: "Send test run notifications and failure analysis results to Slack channels.",
    logo: "/img/integrations/slack.svg",
  },
  {
    key: "jira",
    name: "Jira",
    provider: AuthorizationProvider.Jira,
    description: "Create and link Jira issues directly from test failures for streamlined bug tracking.",
    logo: "/img/integrations/jira.svg",
  },
  {
    key: "claude",
    name: "Claude",
    provider: AuthorizationProvider.Claude,
    description: "Connect your Claude account to power AI-driven test analysis and generation.",
    logo: "/img/integrations/claude.svg",
  },
  {
    key: "spira",
    name: "Spira",
    provider: AuthorizationProvider.Spira,
    description: "Connect your SpiraTest instance to fetch and run tests managed in Spira.",
    logo: "/img/integrations/spira.svg",
  },
  {
    key: "opsgenie",
    name: "OpsGenie",
    provider: AuthorizationProvider.OpsGenie,
    description: "Trigger OpsGenie alerts when test runs fail for on-call incident response.",
    logo: "/img/integrations/opsgenie.svg",
  },
];

function authorizeOpsgenie(token: string) {
  return SettingsStore.createAuthorization(AuthorizationProvider.OpsGenie, token);
}

function doDeleteAuthorization(auth: Authorization) {
  if (auth.installationId && auth.provider === AuthorizationProvider.GitHub) {
    window.open(`https://github.com/organizations/${auth.identifier}/settings/installations/${auth.installationId}`, '_blank', 'noopener,noreferrer');
  } else {
    SettingsStore.deleteAuthorization(auth.id);
  }
}

function deleteAuthorization(name: string, auth: Authorization) {
  if (auth.installationId && auth.provider === AuthorizationProvider.GitHub) {
    ModalStore.confirmation.show(null, {
      message: (
        <div>
          <p>
            To remove this GitHub integration you will have to uninstall the Testery GitHub from
            your organization.
          </p>
          <p>
            To do that <b>you will have to be a GitHub admin in your organization.</b>
          </p>
          <p>
            Click the button below and we'll take you to the GitHub page where you can uninstall the
            app.
          </p>
          <Button
            as="a"
            color="red"
            href={`https://github.com/organizations/${auth.identifier}/settings/installations/${auth.installationId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Remove Testery GitHub App
          </Button>
        </div>
      ),
      header: "Remove GitHub Integration",
      hideOk: true,
      size: "small",
    });
  } else {
    ModalStore.confirmation.show((okClicked: boolean) => okClicked && doDeleteAuthorization(auth), {
      message: `Are you sure you want to remove the ${name} integration?`,
      okText: "Remove",
      okColor: "red",
    });
  }
}

function doAuth(provider: string) {
  Storage.setAuthInfo(
    "/" + AppStore.selectedAccountName + "/settings/integrations",
    AuthReason.Integration,
  );
  repositoryAuthorize(provider);
}

export async function registerEmailAlert() {
  runInAction(() => {
    const form = new AlertForm();
    form.actionType = ActionType.SEND_EMAIL
    form.eventType = EventType.TESTRUN_ANALYSIS_COMPLETE
    form.destination = AppStore.user.email
    SettingsStore.saveAlert(form);
  })
}

export function authorizeSlack() {
  Storage.setAuthInfo(
    "/" + AppStore.selectedAccountName + "/settings/integrations",
    AuthReason.SlackIntegration,
  );
  window.location.href = getSlackAuthUrl();
}

export function authorizeJira() {
  Storage.setAuthInfo(
    "/" + AppStore.selectedAccountName + "/settings/integrations",
    AuthReason.JiraIntegration,
  );
  window.location.href = getJiraAuthUrl();
}


function getOAuthRedirectHandler(provider: AuthorizationProvider) {
  switch (provider) {
    case AuthorizationProvider.GitHub:
      return () => doAuth("github");
    case AuthorizationProvider.Gitlab:
      return () => doAuth("Gitlab");
    case AuthorizationProvider.BitBucket:
      return () => doAuth("bitbucket");
    case AuthorizationProvider.Slack:
      return () => authorizeSlack();
    case AuthorizationProvider.Jira:
      return () => authorizeJira();
    default:
      return undefined;
  }
}

function getOAuthProviderLabel(provider: AuthorizationProvider): string {
  switch (provider) {
    case AuthorizationProvider.GitHub:
      return "GitHub";
    case AuthorizationProvider.Gitlab:
      return "GitLab";
    case AuthorizationProvider.BitBucket:
      return "BitBucket";
    case AuthorizationProvider.Slack:
      return "Slack";
    case AuthorizationProvider.Jira:
      return "Jira";
    default:
      return "";
  }
}

const IntegrationConnectModal = observer(({
  config,
  onClose,
}: {
  config: IntegrationConfig | null;
  onClose: () => void;
}) => {
  const [opsGenieKey, setOpsGenieKey] = useState("");
  const [claudeKey, setClaudeKey] = useState("");
  const [spiraUrl, setSpiraUrl] = useState("");
  const [spiraUser, setSpiraUser] = useState("");
  const [spiraApiKey, setSpiraApiKey] = useState("");
  const [gitHubAccountType, setGitHubAccountType] = useState<"organization" | "personal" | null>(null);

  const handleClose = () => {
    setOpsGenieKey("");
    setClaudeKey("");
    setSpiraUrl("");
    setSpiraUser("");
    setSpiraApiKey("");
    setGitHubAccountType(null);
    bitBucketServerForm.clearFormData();
    azureDevOpsForm.clearFormData();
    microsoftTeamsForm.clearFormData();
    onClose();
  };

  if (!config) return null;

  const isOAuth = oauthProviders.has(config.provider);
  const isGitHub = config.provider === AuthorizationProvider.GitHub;

  const handleOAuthProceed = () => {
    if (isGitHub) {
      Storage.setAuthInfo(
        "/" + AppStore.selectedAccountName + "/settings/integrations",
        AuthReason.Integration,
      );
      doRepositoryAuthorize("github", gitHubAccountType === "personal");
      return;
    }
    const handler = getOAuthRedirectHandler(config.provider);
    if (handler) handler();
  };

  const handleBitBucketServerSubmit = async () => {
    await bitBucketServerForm.submit(async (data) => {
      await SettingsStore.createAuthorization(
        AuthorizationProvider.BitBucketServer,
        data.token,
        data.url,
      );
      bitBucketServerForm.clearFormData();
      onClose();
    });
  };

  const handleAzureDevOpsSubmit = async () => {
    await azureDevOpsForm.submit(async (data) => {
      await SettingsStore.createAuthorization(
        AuthorizationProvider.AzureDevOps,
        data.token,
        data.url.trim().replace(/\/+$/, ""),
      );
      azureDevOpsForm.clearFormData();
      onClose();
    });
  };

  const handleMicrosoftTeamsSubmit = async () => {
    await microsoftTeamsForm.submit(async (data) => {
      await SettingsStore.createAuthorization(
        AuthorizationProvider.MicrosoftTeams,
        data.webhookUrl
      );
      microsoftTeamsForm.clearFormData();
      onClose();
    });
  };

  const handleOpsGenieSubmit = async () => {
    if (!opsGenieKey.trim()) return;
    await authorizeOpsgenie(opsGenieKey);
    setOpsGenieKey("");
    onClose();
  };

  const handleClaudeSubmit = async () => {
    if (!claudeKey.trim()) return;
    await SettingsStore.createAuthorization(AuthorizationProvider.Claude, claudeKey);
    setClaudeKey("");
    onClose();
  };

  const handleSpiraSubmit = async () => {
    if (!spiraUrl.trim() || !spiraUser.trim() || !spiraApiKey.trim()) return;
    await SettingsStore.createAuthorization(
      AuthorizationProvider.Spira,
      spiraApiKey,
      spiraUser,
      spiraUrl,
    );
    setSpiraUrl("");
    setSpiraUser("");
    setSpiraApiKey("");
    onClose();
  };

  const renderModalContent = () => {
    if (isGitHub) {
      return (
        <div className="connect-modal__redirect-notice">
          <p>Are you linking a GitHub Organization or a Personal Account?</p>
          <div className="connect-modal__account-type">
            <Button
              size="small"
              color={gitHubAccountType === "organization" ? "green" : undefined}
              basic={gitHubAccountType !== "organization"}
              onClick={() => setGitHubAccountType("organization")}
            >
              GitHub Organization
            </Button>
            <Button
              size="small"
              color={gitHubAccountType === "personal" ? "green" : undefined}
              basic={gitHubAccountType !== "personal"}
              onClick={() => setGitHubAccountType("personal")}
            >
              Personal Account
            </Button>
          </div>
          <p>
            You will be redirected to <strong>GitHub</strong> to authorize this integration.
            Once complete, you'll be brought back to this page.
          </p>
        </div>
      );
    }

    if (isOAuth) {
      const providerLabel = getOAuthProviderLabel(config.provider);
      return (
        <div className="connect-modal__redirect-notice">
          <p>
            You will be redirected to <strong>{providerLabel}</strong> to authorize this integration.
            Once complete, you'll be brought back to this page.
          </p>
        </div>
      );
    }

    switch (config.provider) {
      case AuthorizationProvider.BitBucketServer:
        return (
          <Form>
            <Form.Field>
              <label>Personal Access Token</label>
              <Form.Input
                value={bitBucketServerForm.token}
                onChange={bitBucketServerForm.onChange("token")}
                error={bitBucketServerForm.getErrorText("token")}
                placeholder="Enter your personal access token"
              />
            </Form.Field>
            <Form.Field>
              <label>Server URL</label>
              <Form.Input
                value={bitBucketServerForm.url}
                onChange={bitBucketServerForm.onChange("url")}
                error={bitBucketServerForm.getErrorText("url")}
                placeholder="https://bitbucket.yourcompany.com"
              />
            </Form.Field>
          </Form>
        );

      case AuthorizationProvider.AzureDevOps:
        return (
          <Form>
            <Form.Field>
              <label>Organization URL</label>
              <Form.Input
                value={azureDevOpsForm.url}
                onChange={azureDevOpsForm.onChange("url")}
                error={azureDevOpsForm.getErrorText("url")}
                placeholder="https://dev.azure.com/your-organization"
                data-testid="azure-devops-url"
              />
            </Form.Field>
            <Form.Field>
              <label>Personal Access Token</label>
              <Form.Input
                type="password"
                value={azureDevOpsForm.token}
                onChange={azureDevOpsForm.onChange("token")}
                error={azureDevOpsForm.getErrorText("token")}
                placeholder="Enter your personal access token"
                data-testid="azure-devops-token"
              />
              <p style={{ marginTop: "0.5rem" }}>
                Create the token in Azure DevOps under User settings, Personal access tokens. It needs
                the <strong>Code (Read)</strong> and <strong>Code (Status)</strong> scopes, plus{" "}
                <strong>Project and Team (Read)</strong> to list your projects.
              </p>
            </Form.Field>
          </Form>
        );

      case AuthorizationProvider.MicrosoftTeams:
        return (
          <Form>
            <Form.Field>
              <label>Incoming Webhook URL</label>
              <Form.Input
                value={microsoftTeamsForm.webhookUrl}
                onChange={microsoftTeamsForm.onChange("webhookUrl")}
                error={microsoftTeamsForm.getErrorText("webhookUrl")}
                placeholder="https://outlook.office.com/webhook/..."
              />
            </Form.Field>
          </Form>
        );

      case AuthorizationProvider.OpsGenie:
        return (
          <Form>
            <Form.Field>
              <label>API Key</label>
              <Input
                type="password"
                value={opsGenieKey}
                onChange={(e) => setOpsGenieKey(e.target.value)}
                fluid
                placeholder="Enter your OpsGenie API key"
              />
            </Form.Field>
          </Form>
        );

      case AuthorizationProvider.Claude:
        return (
          <Form>
            <Form.Field>
              <label>Anthropic API Key</label>
              <Input
                type="password"
                value={claudeKey}
                onChange={(e) => setClaudeKey(e.target.value)}
                fluid
                placeholder="sk-ant-..."
              />
              <p style={{ marginTop: "0.5rem" }}>
                Need a key?{" "}
                <a
                  href="https://docs.anthropic.com/en/api/overview#accessing-the-api"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="claude-api-key-docs-link"
                >
                  See the Anthropic docs on generating an API key
                </a>.
              </p>
            </Form.Field>
          </Form>
        );

      case AuthorizationProvider.Spira:
        return (
          <Form>
            <Form.Field>
              <label>Spira URL</label>
              <Input
                value={spiraUrl}
                onChange={(e) => setSpiraUrl(e.target.value)}
                fluid
                placeholder="https://mycompany.spiraservice.net"
              />
            </Form.Field>
            <Form.Field>
              <label>Username</label>
              <Input
                value={spiraUser}
                onChange={(e) => setSpiraUser(e.target.value)}
                fluid
                placeholder="Your Spira username"
              />
            </Form.Field>
            <Form.Field>
              <label>API Key</label>
              <Input
                type="password"
                value={spiraApiKey}
                onChange={(e) => setSpiraApiKey(e.target.value)}
                fluid
                placeholder="{00000000-0000-0000-0000-000000000000}"
              />
            </Form.Field>
          </Form>
        );

      default:
        return null;
    }
  };

  const renderActions = () => {
    if (isOAuth) {
      return (
        <>
          <Button onClick={handleClose}>Cancel</Button>
          <Button color="green" onClick={handleOAuthProceed} disabled={isGitHub && !gitHubAccountType}>
            <Icon name="external alternate" />
            Proceed
          </Button>
        </>
      );
    }

    switch (config.provider) {
      case AuthorizationProvider.BitBucketServer:
        return (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              color="green"
              onClick={handleBitBucketServerSubmit}
              loading={bitBucketServerForm.isSubmitting}
            >
              Connect
            </Button>
          </>
        );

      case AuthorizationProvider.AzureDevOps:
        return (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              color="green"
              onClick={handleAzureDevOpsSubmit}
              loading={azureDevOpsForm.isSubmitting}
              data-testid="azure-devops-connect"
            >
              Connect
            </Button>
          </>
        );

      case AuthorizationProvider.MicrosoftTeams:
        return (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              color="green"
              onClick={handleMicrosoftTeamsSubmit}
              loading={microsoftTeamsForm.isSubmitting}
            >
              Connect
            </Button>
          </>
        );

      case AuthorizationProvider.OpsGenie:
        return (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              color="green"
              onClick={handleOpsGenieSubmit}
              disabled={!opsGenieKey.trim()}
            >
              Connect
            </Button>
          </>
        );

      case AuthorizationProvider.Claude:
        return (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              color="green"
              onClick={handleClaudeSubmit}
              disabled={!claudeKey.trim()}
            >
              Connect
            </Button>
          </>
        );

      case AuthorizationProvider.Spira:
        return (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              color="green"
              onClick={handleSpiraSubmit}
              disabled={!spiraUrl.trim() || !spiraUser.trim() || !spiraApiKey.trim()}
            >
              Connect
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={!!config} onClose={handleClose} size="small" className="connect-modal">
      <Modal.Header>
        <div className="connect-modal__header">
          <img src={config.logo} alt={`${config.name} logo`} className="connect-modal__logo" />
          Connect {config.name}
        </div>
      </Modal.Header>
      <Modal.Content>
        {renderModalContent()}
      </Modal.Content>
      <Modal.Actions>
        {renderActions()}
      </Modal.Actions>
    </Modal>
  );
});

const IntegrationCard = ({
  config,
  auth,
  onConnect,
}: {
  config: IntegrationConfig;
  auth?: Authorization;
  onConnect: () => void;
}) => {
  const isConnected = !!auth;

  return (
    <div className={`integration-card${isConnected ? " connected" : ""}`}>
      <div className="integration-card__logo">
        <img src={config.logo} alt={`${config.name} logo`} />
      </div>
      <div className="integration-card__body">
        <h3 className="integration-card__name">{config.name}</h3>
        <p className="integration-card__description">{config.description}</p>
      </div>
      {isConnected && (
        <div className="integration-card__status">
          <span className="integration-card__status-badge">
            <Icon name="check circle" /> Connected
          </span>
          {auth.identifier && (
            <span className="integration-card__identifier">{auth.identifier}</span>
          )}
        </div>
      )}
      <div className="integration-card__footer">
        {isConnected ? (
          <Button
            color="red"
            size="small"
            fluid
            onClick={() => deleteAuthorization(config.name, auth)}
          >
            Remove Integration
          </Button>
        ) : (
          <Button color="green" size="small" fluid onClick={onConnect}>
            Connect
          </Button>
        )}
      </div>
    </div>
  );
};

const IntegrationsTab = observer(() => {
  const [connectingConfig, setConnectingConfig] = useState<IntegrationConfig | null>(null);
  const [apiKey, setApiKey] = useState(null);
  const [showUpdateTokenModal, setShowUpdateTokenModal] = useState(false);
  const [isUpdatingToken, setIsUpdatingToken] = useState(false);

  useInitialEffect(async () => {
    bitBucketServerForm.clearFormData();
    azureDevOpsForm.clearFormData();
    await SettingsStore.loadAuthorizations();
    SettingsStore.checkAuthsForOrganizationNeeded();
  });

  const loadApiKey = async () => {
    setApiKey(await SettingsStore.loadApiKey());
  };

  const updateApiKey = async () => {
    setIsUpdatingToken(true);
    setApiKey(await SettingsStore.updateApiKey());
    setIsUpdatingToken(false);
    setShowUpdateTokenModal(false);
  };

  if (!SettingsStore.authorizationsLoaded)
    return (
      <div className="page">
        <div className="page-content">
          <div className="big-spinner">
            <div className="spinner-ring">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </div>
    );

  const { authorizations } = SettingsStore;
  const findAuth = (provider: AuthorizationProvider) =>
    authorizations.find((a) => a.provider === provider);

  return (
    <div className="integrations">
      <div className="integration-grid">
        {integrations.map((config) => (
          <IntegrationCard
            key={config.key}
            config={config}
            auth={findAuth(config.provider)}
            onConnect={() => setConnectingConfig(config)}
          />
        ))}
      </div>

      <div className="api-token-section">
        <h3>API Token</h3>
        {apiKey ? (
          <div>
            <code className="api-token-display">{apiKey}</code>
            <Button onClick={() => setApiKey(null)} size="small">
              <Icon name="eye slash" />
              Hide API Token
            </Button>
            <Button onClick={() => setShowUpdateTokenModal(true)} color="red" size="small">
              <Icon name="sync" />
              Update API Token
            </Button>
          </div>
        ) : (
          <Button color="green" onClick={loadApiKey} size="small">
            <Icon name="eye" inverted />
            Show API Token
          </Button>
        )}
      </div>

      <IntegrationConnectModal
        config={connectingConfig}
        onClose={() => setConnectingConfig(null)}
      />

      <Modal open={showUpdateTokenModal} onClose={() => setShowUpdateTokenModal(false)} size="small" className="connect-modal">
        <Modal.Header>
          <div className="connect-modal__header">
            <Icon name="key" />
            Update API Token
          </div>
        </Modal.Header>
        <Modal.Content>
          <div className="connect-modal__redirect-notice">
            <p>
              Are you sure you want to update your API token? This will <strong>invalidate your old token</strong> and
              any integrations using it will need to be updated.
            </p>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowUpdateTokenModal(false)}>Cancel</Button>
          <Button color="red" onClick={updateApiKey} loading={isUpdatingToken}>
            <Icon name="sync" />
            Update Token
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
});

export default IntegrationsTab;
